import { useMemo, useState } from 'react';
import { ConversationRow, SearchInput, Select } from '../../components';
import { useActions } from '../../queries/actions';
import { ROLE_META, tagColor } from './constants';
import { ConvoScroll, ListEmpty, ListCard, ListTop, Seg, SegBtn, ShowLabel, ShowRow } from './styled';

const matches = (c, q) =>
  !q || `${c.name || ''} ${c.contactNumber} ${c.lastMessage || ''} ${(c.tags || []).join(' ')}`.toLowerCase().includes(q);

const SHOW_OPTIONS = [
  { value: 'all', label: 'All groups' },
  { value: 'prospect', label: 'Prospects' },
  { value: 'tenant', label: 'Tenants' },
  { value: 'owner', label: 'Owners' },
  { value: 'vendor', label: 'Vendors' }
];

export default function ConversationList({ conversations, loading, activeNumber, onSelect }) {
  const [filter, setFilter] = useState('all');
  const [show, setShow] = useState('all');
  const [query, setQuery] = useState('');

  const { rows } = useActions('d');
  const needSet = useMemo(
    () => new Set(rows.filter((x) => x.lane === 'needs' || x.lane === 'review').map((x) => x.contact?.phone).filter(Boolean)),
    [rows]
  );
  const unreadCount = useMemo(() => conversations.filter((c) => c.unread > 0).length, [conversations]);
  const needCount = useMemo(
    () => conversations.filter((c) => needSet.has(c.contactNumber) || c.needsAttention).length,
    [conversations, needSet]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conversations.filter((c) => {
      const needsYou = needSet.has(c.contactNumber) || c.needsAttention;
      const byFilter = filter === 'all' || (filter === 'unread' ? c.unread > 0 : needsYou);
      const byShow = show === 'all' || c.type === show;
      return byFilter && byShow && matches(c, q);
    });
  }, [conversations, filter, show, query, needSet]);

  return (
    <ListCard aria-label="Conversations">
      <ListTop>
        <ShowRow>
          <ShowLabel>Show</ShowLabel>
          <Select
            aria-label="Show group"
            value={show}
            onChange={(e) => setShow(e.target.value)}
            style={{ flex: 1, width: 'auto', minWidth: 0 }}
          >
            {SHOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </ShowRow>
        <Seg role="group" aria-label="Filter conversations">
          <SegBtn type="button" $active={filter === 'all'} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </SegBtn>
          <SegBtn
            type="button"
            $active={filter === 'unread'}
            aria-pressed={filter === 'unread'}
            onClick={() => setFilter('unread')}
          >
            Unread{unreadCount ? ` (${unreadCount})` : ''}
          </SegBtn>
          <SegBtn
            type="button"
            $active={filter === 'needyou'}
            aria-pressed={filter === 'needyou'}
            onClick={() => setFilter('needyou')}
          >
            Need you{needCount ? ` (${needCount})` : ''}
          </SegBtn>
        </Seg>
        <SearchInput value={query} onChange={setQuery} placeholder="Search conversations…" minWidth={0} square />
      </ListTop>
      <ConvoScroll>
        {loading ? (
          <ListEmpty>Loading conversations…</ListEmpty>
        ) : visible.length === 0 ? (
          <ListEmpty>{conversations.length ? 'No conversations match.' : 'No conversations yet.'}</ListEmpty>
        ) : (
          visible.map((c) => (
            <ConversationRow
              key={c.contactNumber}
              conversation={c}
              role={ROLE_META[c.type] || ROLE_META.contact}
              tagColor={tagColor}
              selected={c.contactNumber === activeNumber}
              onSelect={onSelect}
            />
          ))
        )}
      </ConvoScroll>
    </ListCard>
  );
}
